import { useEffect, useRef } from 'react';

export interface UsePollerOptions {
  /** Poller is active only while true. Toggling false→true restarts it. */
  enabled: boolean;
  /** Optional identity key — changing it restarts the poll loop even if
   *  `enabled` stays true (e.g. a new run/job id superseding the old one). */
  resetKey?: string | number;
  /** ms until the next tick. Called fresh before every schedule so callers
   *  can vary cadence (idle backoff) without restarting the effect. */
  intervalMs: () => number;
  /** One poll tick. Return 'stop' once the polled thing reached a terminal
   *  state — the loop won't schedule another tick. Throw to signal a
   *  failed tick (counted toward `maxConsecutiveErrors`). */
  tick: () => Promise<'continue' | 'stop'>;
  /** If true, the first tick fires immediately instead of after the first
   *  `intervalMs()` delay. Default false (mirrors the common case where the
   *  caller already has fresh state going into the poll). */
  immediate?: boolean;
  /** Optional hard wall-clock deadline (epoch ms). Checked before every
   *  scheduled tick (including the first); once passed, the loop stops and
   *  `onDeadline` fires instead of ticking again. */
  deadlineAtMs?: number;
  onDeadline?: () => void;
  /** Consecutive failed ticks allowed before giving up. Default 5. */
  maxConsecutiveErrors?: number;
  onGiveUp?: (error: unknown) => void;
}

/**
 * Bounded, cancellation-safe poller — self-rescheduling `setTimeout` (never
 * `setInterval`, so a slow tick can't overlap the next one), an optional
 * wall-clock deadline, and a consecutive-error cutoff. Modeled on
 * `LearningPage.tsx`'s migrate-conversations poll loop (the in-repo
 * "correct pattern" OP-301 calls out) — extracted so Discovery / bank-
 * operation / sources / ticket-imports / knowledge-editor pollers stop
 * duplicating the same setTimeout+cancelled-ref boilerplate, several
 * copies of which had no deadline and no error backoff at all
 * (OP-301/303/304/329/331).
 *
 * Ported verbatim from admin-portal/src/hooks/usePoller.ts (the agent console uses it for the 60 s counts + drafts poll).
 *
 * Callers own their own state (via closures in `tick`); this hook only
 * owns the scheduling loop's lifecycle.
 */
export function usePoller(options: UsePollerOptions): void {
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!options.enabled) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let consecutiveErrors = 0;

    const checkDeadline = (): boolean => {
      const { deadlineAtMs, onDeadline } = optionsRef.current;
      if (deadlineAtMs !== undefined && Date.now() > deadlineAtMs) {
        onDeadline?.();
        return true;
      }
      return false;
    };

    const scheduleNext = () => {
      if (cancelled || checkDeadline()) return;
      timer = setTimeout(() => void runTick(), optionsRef.current.intervalMs());
    };

    const runTick = async () => {
      if (cancelled) return;
      // Re-check: this tick may have been scheduled before the deadline
      // passed but is only now firing.
      if (checkDeadline()) return;
      const { tick, maxConsecutiveErrors = 5, onGiveUp } = optionsRef.current;
      try {
        const result = await tick();
        if (cancelled) return;
        consecutiveErrors = 0;
        if (result === 'stop') return;
        scheduleNext();
      } catch (err) {
        if (cancelled) return;
        consecutiveErrors += 1;
        if (consecutiveErrors >= maxConsecutiveErrors) {
          onGiveUp?.(err);
          return;
        }
        scheduleNext();
      }
    };

    if (!checkDeadline()) {
      if (optionsRef.current.immediate) void runTick();
      else scheduleNext();
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.enabled, options.resetKey]);
}
